import { ArrowRight } from "lucide-react";
import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Card } from "@/components/ui/card";
import { useAuthStore } from "@/features/auth/store/auth-store";
import { listMyOnboarding, type MyOnboardingClient } from "@/features/clients/member-onboarding-api";

/**
 * Dashboard widget: the clients assigned to the current Team Leader / Staff
 * member that still need their onboarding steps. Role-gated (TL/Staff only) and
 * hidden when there's nothing to onboard.
 */
export function MyOnboardingCard() {
  const role = useAuthStore((s) => s.user?.role);
  const enabled = role === "team_leader" || role === "staff";
  const [items, setItems] = useState<MyOnboardingClient[] | null>(null);

  useEffect(() => {
    if (!enabled) return;
    let active = true;
    listMyOnboarding()
      .then((r) => active && setItems(r))
      .catch(() => active && setItems([]));
    return () => {
      active = false;
    };
  }, [enabled]);

  if (!enabled || !items || items.length === 0) return null;

  return (
    <Card className="gap-4 p-5">
      <h2 className="text-xl font-semibold">My clients to onboard</h2>
      <div className="flex flex-col gap-1">
        {items.map((c) => (
          <Link
            key={c.id}
            to={`/client/${c.id}/overview`}
            className="hover:bg-muted flex items-center gap-3 rounded-md px-2 py-2 text-sm"
          >
            <span className="min-w-0 flex-1 truncate font-medium">{c.name}</span>
            <span className="text-muted-foreground shrink-0 text-xs tabular-nums">
              {c.completed_steps}/{c.total_steps}
            </span>
            <ArrowRight className="text-muted-foreground size-4 shrink-0" />
          </Link>
        ))}
      </div>
    </Card>
  );
}
